import dynamic from 'next/dynamic';
import { memo, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSceneSelected } from '@containers/Home/selectors';
import { TourRenderSceneFlat360Wrapper } from './styled';
import LoadingContainer from '@components/LoadingContainer';

const Action = dynamic(() => import('./Action'), { ssr: false });

export interface I_ViewerState {
  zoomLevel: number;
  imagesLoaded: number;
  currentFrame: number;
  minZoomLevel: number;
  maxZoomLevel: number;
  isLoading: boolean;
  isDragging: boolean;
  isAnimating: boolean;
}

interface Props {
  setIsTourRendered: (isLoaded: boolean) => void;
}

export const TourRenderSceneFlat360 = memo(({ setIsTourRendered }: Props) => {
  const lastXRef = useRef(0);
  const startYRef = useRef(0);
  const viewerRef = useRef<HTMLDivElement>(null!);
  const autorotateIntervalRef = useRef(null);

  const keyboardControls = true;
  const highResZoomThreshold = 2.5;
  const reverseDirection = false;

  const scene = useSelector(selectSceneSelected);

  // Group related state
  const [viewerState, setViewerState] = useState<I_ViewerState>({
    zoomLevel: 1,
    imagesLoaded: 0,
    currentFrame: 1,
    minZoomLevel: 1,
    maxZoomLevel: 5,
    isLoading: true,
    isDragging: false,
    isAnimating: false,
  });
  const [images, setImages] = useState<HTMLImageElement[]>([]);
  const [highResImages, setHighResImages] = useState<HTMLImageElement[]>([]);

  const totalFrames = Number(scene?.object360?.totalImages) ?? 1;
  const loadingProgress = useMemo(
    () => Math.round((viewerState.imagesLoaded / totalFrames) * 100),
    [viewerState.imagesLoaded, totalFrames],
  );
  const useHighRes = useMemo(
    () =>
      scene?.object360?.lvl &&
      viewerState.zoomLevel > highResZoomThreshold &&
      highResImages[viewerState.currentFrame],
    [
      highResImages,
      scene?.object360?.lvl,
      viewerState.zoomLevel,
      viewerState.currentFrame,
    ],
  );
  const currentImageSrc = useMemo(
    () =>
      useHighRes
        ? highResImages[viewerState.currentFrame - 1]?.src ||
          images[viewerState.currentFrame - 1]?.src
        : images[viewerState.currentFrame - 1]?.src,
    [useHighRes, highResImages, viewerState.currentFrame, images],
  );

  const onZooming = useCallback(
    e => {
      e.preventDefault();
      const delta = e.deltaY * -0.01;
      const zoomLevel = Math.max(
        viewerState.minZoomLevel,
        Math.min(viewerState.maxZoomLevel, viewerState.zoomLevel + delta),
      );
      setViewerState(prev => ({
        ...prev,
        zoomLevel,
      }));
    },
    [viewerState.minZoomLevel, viewerState.maxZoomLevel, viewerState.zoomLevel],
  );

  const startDragging = useCallback(
    e => {
      if (viewerState.isLoading || viewerState.isAnimating) return;
      if (autorotateIntervalRef.current)
        clearInterval(autorotateIntervalRef.current);
      setViewerState(prev => ({ ...prev, isDragging: true }));
      startYRef.current = e.clientY;
      lastXRef.current = e.clientX;
    },
    [viewerState.isLoading, viewerState.isAnimating],
  );

  const onDragging = useCallback(
    e => {
      if (
        !viewerState.isDragging ||
        viewerState.isLoading ||
        viewerState.isAnimating
      )
        return;

      const currentX = e.clientX;
      const currentY = e.clientY;
      const diffX = currentX - lastXRef.current;
      const diffY = currentY - startYRef.current;
      lastXRef.current = currentX;

      if (diffX !== 0 || diffY !== 0) {
        const frameStep = Math.floor(Math.abs(diffX) / 5) || 1;
        const directionX = (diffX < 0 ? 1 : -1) * (reverseDirection ? -1 : 1);
        // const directionY = (diffY < 0 ? 1 : -1) * (reverseDirection ? -1 : 1);

        setViewerState(prev => {
          let newFrame = prev.currentFrame + directionX * frameStep;
          while (newFrame > totalFrames) newFrame -= totalFrames;
          while (newFrame < 1) newFrame += totalFrames;
          return { ...prev, currentFrame: newFrame };
        });
      }
    },
    [
      totalFrames,
      reverseDirection,
      viewerState.isLoading,
      viewerState.isDragging,
      viewerState.isAnimating,
    ],
  );

  const endDragging = useCallback(() => {
    setViewerState(prev => ({ ...prev, isDragging: false }));
  }, []);

  // Preload all images (standard resolution only)
  useEffect(() => {
    setViewerState(prev => ({
      ...prev,
      ...scene?.object360,
      isLoading: true,
      imagesLoaded: 0,
    }));
    const imageObjects: HTMLImageElement[] = [];
    const highResImageObjects: HTMLImageElement[] = [];
    let loadedCount = 0;

    const loadImage = (frameNumber, isHighRes = false) => {
      const img = new Image();
      const formattedFrame = frameNumber
        .toString()
        .padStart(scene?.object360?.padLength || 5, '0');
      img.src = `${process.env.NEXT_PUBLIC_AWS_URL}/${
        scene?.object360?.lvl && scene?.object360?.lvl.length
          ? isHighRes && scene?.object360?.lvl.length > 1
            ? scene?.object360?.lvl[1]
            : scene?.object360?.lvl[0]
          : scene?.object360?.imagePath
      }${scene?.object360?.imagePrefix}${formattedFrame}.${scene?.object360
        ?.imageExt}`;

      img.onload = () => {
        if (!isHighRes) {
          loadedCount++;
          setViewerState(prev => ({ ...prev, imagesLoaded: loadedCount }));
        }
        if (loadedCount === totalFrames) {
          setViewerState(prev => ({ ...prev, isLoading: false }));
          setIsTourRendered(true);
        }
      };

      img.onerror = err => {
        console.error(`Failed to load image ${img.src}:`, err);
      };

      return img;
    };

    for (let i = 1; i <= totalFrames; i++) {
      imageObjects.push(loadImage(i));
      highResImageObjects.push(loadImage(i, true));
    }

    setImages(imageObjects);
    setHighResImages(highResImageObjects);

    return () => {
      imageObjects.forEach(img => {
        img.onload = null;
        img.onerror = null;
      });
      highResImageObjects.forEach(img => {
        img.onload = null;
        img.onerror = null;
      });
      setImages([]);
      setHighResImages([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene?.id]);

  // Keyboard controls
  useEffect(() => {
    if (
      !keyboardControls ||
      viewerState.isAnimating
      // || hotspotState.isHotspotEditMode || hotspotState.selectedHotspot
    )
      return;

    const handleKeyDown = e => {
      const direction = reverseDirection ? -1 : 1;
      switch (e.key) {
        case 'ArrowLeft':
          setViewerState(prev => {
            return {
              ...prev,
              currentFrame:
                (prev.currentFrame - direction) % totalFrames || totalFrames,
            };
          });
          break;
        case 'ArrowRight':
          setViewerState(prev => {
            return {
              ...prev,
              currentFrame:
                (prev.currentFrame + direction) % totalFrames || totalFrames,
            };
          });
          break;
        case 'ArrowUp':
          setViewerState(prev => ({
            ...prev,
            zoomLevel: Math.min(prev.maxZoomLevel, prev.zoomLevel + 0.1),
          }));
          break;
        case 'ArrowDown':
          setViewerState(prev => ({
            ...prev,
            zoomLevel: Math.max(prev.minZoomLevel, prev.zoomLevel - 0.1),
          }));
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardControls, viewerState.isAnimating]);

  useEffect(() => {
    const container = viewerRef.current;
    if (container)
      container.addEventListener('wheel', onZooming, { passive: false });
    return () => {
      if (container) container.removeEventListener('wheel', onZooming);
    };
  }, [onZooming]);

  return (
    <LoadingContainer
      loading={viewerState.isLoading}
      description={`Loading... ${loadingProgress}%`}
    >
      <TourRenderSceneFlat360Wrapper
        ref={viewerRef}
        onMouseDown={startDragging}
        onMouseMove={onDragging}
        onMouseUp={endDragging}
        onMouseLeave={endDragging}
        style={{ cursor: viewerState.isDragging ? 'grabbing' : 'grab' }}
      >
        {currentImageSrc && (
          <div
            className="--image-container"
            style={{
              transform: `scale(${viewerState.zoomLevel})`,
            }}
          >
            <img
              src={currentImageSrc}
              alt={`Frame ${viewerState.currentFrame}`}
              className="--image"
              draggable={false}
            />
          </div>
        )}
        <Action
          scene={scene}
          viewerState={viewerState}
          setViewerState={setViewerState}
          totalFrames={totalFrames}
        />
      </TourRenderSceneFlat360Wrapper>
    </LoadingContainer>
  );
});
